/**
 * The pieces the share modal and the default-sharing setting have in common.
 *
 * Both ask the same question — who may see this, and at what level — so they
 * render the same picker, the same access list and the same general-access
 * control. They differ in what they are choosing for: the share modal edits one
 * entity and writes on every change, while the setting edits a default for
 * notes that do not exist yet and writes once, on Save (#11877).
 */
import type { SharePayload, SharingStatusResponse } from "@/services/sharing";

export type GeneralAccess = "restricted" | "organization" | "link";

/**
 * The organization as General access names it: "Acme Organization" for Acme, and a name that
 * already ends in the word left as it is, never "Acme Organization Organization" (UX walk item
 * 26, dashboard-assistant-polish-4 requirement 19).
 */
export const organizationLabel = (name: string): string =>
  /\borganization$/i.test(name.trim()) ? name.trim() : `${name} Organization`;

/** What each general-access level is called: "Restricted", the
 *  organization's name, "Anyone with the link". */
export const generalAccessLabel = (
  access: GeneralAccess,
  organizationName: string
): string =>
  access === "restricted"
    ? "Restricted"
    : access === "organization"
      ? organizationName
      : "Anyone with the link";

/**
 * Whether the viewer owns the entity. When the backend resolves an owner,
 * trust it. When it can't, don't optimistically assume the current user is
 * the owner. Imported interactions are authored by a system/cross-org account
 * (e.g. "Finpilot Administrator") that resolves to no real org user, so the
 * backend returns owner=null — in that case nobody is listed as owner and the
 * current user is shown as a plain access-holder. Views/workspaces never
 * return an owner, so they keep using the caller's `isOwner` fallback. #9953
 */
export const resolveIsOwner = (
  status: SharingStatusResponse | undefined,
  userId: string | null | undefined,
  entityType: string,
  isOwner: boolean
): boolean => {
  const owner = status?.owner ?? null;
  if (owner) return owner.id === userId;
  return entityType === "interaction" ? false : isOwner;
};

/** Whether the viewer may change the entity's sharing: the backend's answer
 *  when it gives one (#9954), else whether they own it. */
export const canManageSharingOf = (
  status: SharingStatusResponse | undefined,
  resolvedIsOwner: boolean
): boolean => status?.can_manage_sharing ?? resolvedIsOwner;

/** The viewer can only read the entity: they hold view access and do not
 *  own it. Such a viewer gets no sharing controls at all. */
export const isViewOnly = (
  status: SharingStatusResponse | undefined,
  resolvedIsOwner: boolean
): boolean =>
  (status?.current_user_permission ?? "edit") === "view" && !resolvedIsOwner;

/** Whether the viewer may change the entity's general access (restricted,
 *  the organization, the link): not view-only, allowed to change sharing,
 *  and not refused the organization grant. */
export const canChangeGeneralAccess = (
  status: SharingStatusResponse | undefined,
  resolvedIsOwner: boolean
): boolean =>
  !isViewOnly(status, resolvedIsOwner) &&
  canManageSharingOf(status, resolvedIsOwner) &&
  status?.can_manage_organization_sharing !== false;

/** The entity's general access, read from its sharing status. */
export const generalAccessOf = (
  status: SharingStatusResponse | undefined
): GeneralAccess => {
  if (!status) return "restricted";
  if (status.link_sharing) return "link";
  if (status.org_id) return "organization";
  return "restricted";
};

/** The sentence under the general-access choice saying what it means. */
export const generalAccessCaption = (
  access: GeneralAccess,
  organizationName: string,
  orgPermission: string | null | undefined
): string =>
  access === "restricted"
    ? "Only people with access can view"
    : access === "organization"
      ? `Anyone in ${organizationName} can ${orgPermission === "view" ? "view" : "edit"}`
      : "Anyone with this link in the organization can view";

/**
 * What a change of general access writes: the organization at `orgJoinsAs`,
 * the link, or neither. The share modal (#15177: and the phone's share sheet)
 * sends these as overrides of the entity's current grants.
 */
export const generalAccessOverrides = (
  access: GeneralAccess,
  orgJoinsAs: "view" | "edit"
): Partial<SharePayload> =>
  access === "restricted"
    ? { organization: null, link_sharing: false }
    : access === "organization"
      ? { organization: { type: orgJoinsAs }, link_sharing: false }
      : { organization: null, link_sharing: true };

/**
 * The whole sharing write for an entity: its current people, teams,
 * organization and link, with `overrides` in place of any of them, and the
 * `updated_at` the server checks for a concurrent change.
 */
export const sharePayloadFor = (
  status: SharingStatusResponse | undefined,
  overrides: Partial<SharePayload>
): SharePayload => {
  const users =
    overrides.users ??
    status?.users.map((u) => ({ id: u.id, type: u.type })) ??
    [];
  const teams =
    overrides.teams ??
    status?.teams.map((t) => ({ id: t.id, type: t.type })) ??
    [];
  const organization =
    overrides.organization !== undefined
      ? overrides.organization
      : status?.org_id
        ? {
            type: (status.org_permission ?? "edit") as "view" | "edit",
          }
        : null;
  const linkSharing =
    overrides.link_sharing !== undefined
      ? overrides.link_sharing
      : (status?.link_sharing ?? false);
  return {
    users,
    teams,
    organization,
    link_sharing: linkSharing,
    ...(status?.updated_at ? { updated_at: status.updated_at } : {}),
  };
};

/** What the picker needs to offer someone: enough to show and identify them. */
export interface PickableUser {
  id: string;
  name: string;
  email?: string | null;
}

export interface PickableTeam {
  id: string;
  name: string;
}

export interface AccessItem {
  id: string;
  name: string;
  email?: string;
  /** "organization" only for access inherited from a view group (#13896):
   *  everyone in the organization, through the group. */
  kind: "user" | "team" | "organization";
  role: "Owner" | "Editor" | "Viewer";
  permissionType: "view" | "edit";
  /**
   * Set when the access comes through another entity (the agent that
   * produced an artifact, #11941; a view group that holds a view, #13896):
   * named in the row, and neither the role nor the removal is offered, since
   * it is not this entity's grant.
   */
  inheritedFrom?: string;
  /** What `inheritedFrom` is: an agent reads "via <agent>", a view group
   *  or an artifact's folder (#14320) "<Level>, from <group or folder>". */
  inheritedKind?: "agent" | "group" | "folder";
  /**
   * A member added but not yet saved (a request for access, round 10 U4): the row says so until
   * Save sends the grant.
   */
  unsaved?: boolean;
}

/** Two initials for an avatar, from whatever the name gives us. */
export const getInitials = (name: string): string =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
